/**
 * Состояние входа в шапке (ТЗ UI-60 … UI-63).
 *
 * Четыре состояния, и каждое отличается тремя вещами сразу: словом, формой
 * знака и его заливкой. Цветом единственным носителем состояние не бывает
 * (ТЗ UI-09), а слово — единственное, что останется, если стили не
 * загрузились.
 *
 * Адреса почты здесь нет намеренно (ТЗ UI-61): шапка видна всем, кто смотрит
 * на экран через плечо, а полный адрес человек и так видит на экране
 * аккаунта. Не пишется и «сохранено»: подтверждения от сервера у шапки нет,
 * а обещать сохранность, которой не знаешь, нельзя.
 */

import type { MessageKey, Translate } from '@type2x/i18n';

import type { AccountStatus } from '../hooks/useAccountStatus.js';

/** Ключи слова и полного объявления для каждого состояния. */
const LABELS: Record<AccountStatus, { readonly text: MessageKey; readonly aria: MessageKey }> = {
  unknown: { text: 'nav.account.checking', aria: 'nav.account.checkingAria' },
  guest: { text: 'nav.account.guest', aria: 'nav.account.guestAria' },
  'signed-in': { text: 'nav.account.signedIn', aria: 'nav.account.signedInAria' },
  unverified: { text: 'nav.account.unverified', aria: 'nav.account.unverifiedAria' },
};

/**
 * Знак состояния: оригинальный, в одну краску `currentColor` (ТЗ UI-64,
 * UI-65).
 *
 * Формы разные по существу, а не по цвету: пунктирный круг — про нас ещё не
 * знают; сплошной круг с галочкой — вход есть и адрес подтверждён; сплошной
 * круг с восклицательным знаком — вход есть, а подтверждения нет; ровный круг
 * без знака — ответа пока нет.
 */
function StatusMark({ status }: { readonly status: AccountStatus }): React.JSX.Element {
  return (
    <svg
      className="t2x-account-status__mark"
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
    >
      {status === 'guest' && (
        <circle
          cx="8"
          cy="8"
          r="6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeDasharray="3 2"
        />
      )}
      {status === 'unknown' && (
        <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
      )}
      {status === 'signed-in' && (
        <>
          <circle cx="8" cy="8" r="7" fill="currentColor" />
          <path
            d="M4.5 8.2l2.4 2.4 4.6-4.8"
            fill="none"
            stroke="var(--t2x-mark-fg)"
            strokeWidth="1.8"
            strokeLinecap="square"
          />
        </>
      )}
      {status === 'unverified' && (
        <>
          <circle cx="8" cy="8" r="7" fill="currentColor" />
          <rect x="7.1" y="3.6" width="1.8" height="5.4" fill="var(--t2x-mark-fg)" />
          <rect x="7.1" y="10.4" width="1.8" height="1.8" fill="var(--t2x-mark-fg)" />
        </>
      )}
    </svg>
  );
}

export interface AccountStatusButtonProps {
  readonly t: Translate;
  readonly status: AccountStatus;
  readonly onOpenAccount: () => void;
}

export function AccountStatusButton({
  t,
  status,
  onOpenAccount,
}: AccountStatusButtonProps): React.JSX.Element {
  const label = LABELS[status];

  return (
    <button
      type="button"
      className={`t2x-account-status t2x-account-status--${status}`}
      aria-label={t(label.aria)}
      onClick={onOpenAccount}
    >
      <StatusMark status={status} />
      {/* Слово остаётся видимым и на телефоне: это единственное, что
          отличает состояния для того, кто знака не разглядит (ТЗ UI-63). */}
      <span>{t(label.text)}</span>
    </button>
  );
}
